import { useCallback, useRef } from 'react';
import { Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useBackButton } from '../hooks/useBackButton';
import { useTelegramViewport } from '../hooks/useTelegramViewport';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/shared/PullToRefreshIndicator';
import InvitationsSection, {
  type InvitationsSectionHandle,
} from '../components/providers/InvitationsSection';
import ProvidersListSection, {
  type ProvidersListSectionHandle,
} from '../components/providers/ProvidersListSection';

interface Props {
  onBack: () => void;
}

export default function ProvidersPage({ onBack }: Props) {
  const { t } = useTranslation('owner');
  const stableOnBack = useCallback(onBack, []);
  useBackButton(stableOnBack);
  const viewportHeight = useTelegramViewport();

  const invitationsRef = useRef<InvitationsSectionHandle>(null);
  const providersRef = useRef<ProvidersListSectionHandle>(null);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      invitationsRef.current?.refresh() ?? Promise.resolve(),
      providersRef.current?.refresh() ?? Promise.resolve(),
    ]);
  }, []);

  const { containerRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({ onRefresh: handleRefresh });

  return (
    <div
      style={{
        height: viewportHeight,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bl-bg, #f5f7fa)',
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: '12px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'var(--bl-surface, #fff)',
          borderBottom: '1px solid var(--bl-divider, #ebebeb)',
          paddingTop: 'max(12px, env(safe-area-inset-top, 12px))',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            background: 'var(--bl-surface-soft, #f0f4f8)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            color: 'var(--bl-text-strong, #0f172a)',
            flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <ArrowLeftOutlined />
        </button>

        <Typography.Text
          style={{
            fontSize: 18,
            fontWeight: 800,
            color: 'var(--bl-text-strong, #0f172a)',
            letterSpacing: -0.3,
          }}
        >
          {t('providers.title')}
        </Typography.Text>
      </div>

      {/* Scrollable content with pull-to-refresh */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          position: 'relative',
        }}
      >
        <PullToRefreshIndicator
          pullDistance={pullDistance}
          isRefreshing={isRefreshing}
          progress={progress}
        />

        <div
          style={{
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <InvitationsSection ref={invitationsRef} />
          <ProvidersListSection ref={providersRef} />
        </div>
      </div>
    </div>
  );
}
